import { Typography, Box, Divider, Grid, Alert, Button } from "@mui/material";
import {
	CalendarToday as CalendarIcon,
	Add as AddIcon,
} from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { fetchCompanyUsersRequest } from "../store/slices/database/companyUsersSlice";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useDispatch, useSelector } from "react-redux";
import { addCompanyUserThunk, fetchAssignableRolesThunk, editCompanyUserThunk, updateCompanyUserRolesThunk, removeCompanyUserThunk } from "../store/thunks/mdsThunks";
import { logger } from "helpers/logger";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import { manageUsersReducer, initialState } from "../reducers/manageUsersReducer";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import { useTableColumns } from "../hooks/useTableColumns";
import { useFormFields } from "../hooks/useFormFields";

// The member's current value of a form field: row[storage][target], where the config says it is stored
// (identity / details / companyDetails, each keyed by column). No field names in code.
const currentFieldValue = (row, field) => {
	const value = row?.[field.storage]?.[field.target];
	return value == null ? "" : String(value);
};

export default function ManageUsersPage() {
	const keycloakAuth = useAuth();
	const dispatch = useDispatch();
	const [t] = useTranslation();

	const { showToast,success,error:ToastError,warning,info, } = useToast();
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

	// One selector per value. The slice keeps the whole API response in data: the list is data.data.
	const companyUsersData = useSelector((state) => state.companyUsers?.data?.data);
	const loading = useSelector((state) => state.companyUsers?.loading);
	const error = useSelector((state) => state.companyUsers?.error);
	const selectedCompanyId = selectedCompany?.companyId;

	const user = keycloakAuth.user;
	const userId = user?.id;

	// Add state to track deleted record IDs
	const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());

	// Use reducer for dialog and form state management
	const [state, dispatchState] = useReducer(manageUsersReducer, initialState);

	// Ref to track ongoing thunk requests
	const activeThunksRef = useRef([]);

	// Live members of the selected company (authz). For search: rolesLabel = the role names, detailsText =
	// the values of the client-specific fields of the person and of the person in this company (whatever
	// columns tbl_user_details / tbl_user_company_details have for this client).
	const displayData = useMemo(
		() =>
			(companyUsersData || [])
				.filter((record) => !deletedRecordIds.has(record.userId))
				.map((record) => ({
					...record,
					rolesLabel: (record.roles || []).map((r) => t(r.displayName || r.roleCode)).join(", "),
					detailsText: [...Object.values(record.details || {}), ...Object.values(record.companyDetails || {})]
						.filter((v) => v != null && typeof v !== "object")
						.join(" "),
				})),
		[companyUsersData, deletedRecordIds, t]
	);

	// Load the members of the selected company, and again whenever the selected company changes.
	useEffect(() => {
		if (selectedCompanyId) {
			dispatch(fetchCompanyUsersRequest({ companyId: selectedCompanyId }));
		}
	}, [dispatch, selectedCompanyId]);

	// A successful reload is the server's truth: rows hidden since a removal are no longer needed.
	// Offline the reload fails, the list doesn't change, hidden rows stay hidden.
	useEffect(() => {
		setDeletedRecordIds((prev) => (prev.size ? new Set() : prev));
	}, [companyUsersData]);

	// Cleanup function to abort all active thunks on unmount
	useEffect(() => {
		return () => {
			// Abort all active thunks when component unmounts
			activeThunksRef.current.forEach((thunk) => {
				if (thunk && typeof thunk.abort === "function") {
					thunk.abort();
				}
			});
			activeThunksRef.current = [];
		};
	}, []);

	// #region SEARCH & PAGINATION
	const { setSearchQuery, filteredData } = useSearchFilter(displayData, ["fullName", "username", "email", "detailsText", "rolesLabel"], null);

	// Local pagination
	const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

	// Fewer pages than the current one (after a removal or a narrower search): go to the last page.
	useEffect(() => {
		if (state.table.currentPage > totalPages) {
			dispatchState({ type: "SET_CURRENT_PAGE", payload: totalPages });
		}
	}, [state.table.currentPage, totalPages]);

	const pagedData = useMemo(() => {
		const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
		const end = start + state.table.recordsPerPage;
		return (filteredData || []).slice(start, end);
	}, [filteredData, state.table.currentPage, state.table.recordsPerPage]);

	// #endregion

	// #region ASSIGN/MODIFY ROLES PROCESS
	// Which roles the member is assigned in this company. The picker offers only roles the caller may
	// assign; the member's current roles (from the list row) are preselected. Saving sends the complete
	// new set: roles left out are unassigned from the member, new ones assigned. None is allowed.
	const handleModifyRolesClick = useCallback(
		async (row) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			const thunkPromise = dispatch(fetchAssignableRolesThunk({ companyId: selectedCompanyId }));
			activeThunksRef.current.push(thunkPromise);
			const result = await thunkPromise;
			activeThunksRef.current = activeThunksRef.current.filter((p) => p !== thunkPromise);
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });

			if (!fetchAssignableRolesThunk.fulfilled.match(result)) {
				if (result.payload !== "cancelled") {
					ToastError(result.payload || t("Failed to load the roles"), { severity: "error" });
				}
				return;
			}

			dispatchState({
				type: "OPEN_MODIFY_ROLES_DIALOG",
				payload: {
					record: row,
					roleOptions: (result.payload?.data || []).map((r) => ({ label: t(r.displayName || r.roleCode), value: r.roleId })),
					initialRoles: (row.roles || []).map((r) => r.roleId),
				},
			});
		},
		[dispatch, selectedCompanyId, ToastError, t]
	);

	const handleCancelModifyRoles = useCallback(() => {
		dispatchState({ type: "CLOSE_MODIFY_ROLES_DIALOG" });
	}, []);

	const handleConfirmModifyRoles = useCallback(
		async (changedFields, fullData) => {
			const record = state.dialogs.selectedRecord;
			if (!record || !changedFields?.Roles) {
				dispatchState({ type: "CLOSE_MODIFY_ROLES_DIALOG" });
				return;
			}

			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			try {
				const result = await dispatch(
					updateCompanyUserRolesThunk({ companyId: selectedCompanyId, userId: record.userId, body: { roleIds: fullData?.Roles || [] } })
				);

				if (updateCompanyUserRolesThunk.fulfilled.match(result)) {
					if (result.payload?.queued) {
						success(t("Roles update saved offline — will sync when reconnected"));
					} else if (result.payload?.data?.rolesSyncedToKeycloak === false) {
						// Saved and in effect in the app; only the Keycloak copy of the roles is behind.
						warning(t("Roles updated. They could not be copied to Keycloak yet."));
					} else {
						success(t("User roles has been updated successfully"));
					}
					// The list is reloaded by the thunk.
					dispatchState({ type: "CLOSE_MODIFY_ROLES_DIALOG" });
				} else {
					logger.error("Failed to update user roles:", result.payload);
					ToastError(result.payload || t("Failed to update the roles"), { severity: "error" });
				}
			} catch (e) {
				logger.error("Unexpected error in handleConfirmModifyRoles:", e);
				ToastError(t("An unexpected error occurred"), { severity: "error" });
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[state.dialogs.selectedRecord, dispatch, selectedCompanyId, success, warning, ToastError, t]
	);
	//#endregion

	// #region Add PROCESS
	// The dialog's fields come from the application's form config (tbl_form_field_configs, "AddUser"),
	// the same rows the backend validates against. No config = no form: the user is told so at once.
	const {
		fields: addFormFields,
		loading: addFormLoading,
		error: addFormError,
		notConfigured: addFormNotConfigured,
		reload: reloadAddForm,
	} = useFormFields("AddUser");

	// The roles field (storage "roles") gets the roles the caller may assign; the others come as configured.
	const addDialogFields = useMemo(
		() =>
			addFormFields.map((field) =>
				field.storage === "roles"
					? { ...field, type: "dropdown", multiple: true, options: state.ui.assignableRoleOptions, isLoading: state.ui.assignableRolesLoading }
					: field
			),
		[addFormFields, state.ui.assignableRoleOptions, state.ui.assignableRolesLoading]
	);

	const handleAddClick = async () => {
		if (addFormNotConfigured) {
			warning(t("The Add user form is not configured for this application"));
			return;
		}
		if (addFormError) {
			ToastError(t("The Add user form could not be loaded. Please try again."), { severity: "error" });
			reloadAddForm();
			return;
		}
		if (addFormLoading || addFormFields.length === 0) {
			info(t("The form is still loading, please try again in a moment."));
			return;
		}

		const defaultValues = Object.fromEntries(addFormFields.map((f) => [f.name, f.storage === "roles" ? [] : ""]));
		dispatchState({ type: "OPEN_ADD_DIALOG", payload: { defaultValues } });

		// Only roles the caller may assign are offered (the backend refuses any other).
		const thunkPromise = dispatch(fetchAssignableRolesThunk({ companyId: selectedCompanyId }));
		activeThunksRef.current.push(thunkPromise);
		const result = await thunkPromise;
		activeThunksRef.current = activeThunksRef.current.filter((p) => p !== thunkPromise);

		if (fetchAssignableRolesThunk.fulfilled.match(result)) {
			const options = (result.payload?.data || []).map((r) => ({ label: t(r.displayName || r.roleCode), value: r.roleId }));
			dispatchState({ type: "SET_ASSIGNABLE_ROLES", payload: options });
		} else {
			dispatchState({ type: "SET_ASSIGNABLE_ROLES", payload: [] });
			if (result.payload !== "cancelled") {
				ToastError(result.payload || t("Failed to load the roles"), { severity: "error" });
			}
		}
	};

	const handleConfirmAdd = useCallback(
		async (formData) => {
			// Field values by field key, roles apart. The backend decides where each value is stored.
			const fields = {};
			let roleIds = [];
			addFormFields.forEach((field) => {
				if (field.storage === "roles") {
					roleIds = formData[field.name] || [];
				} else {
					fields[field.name] = formData[field.name] ?? "";
				}
			});

			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			try {
				const result = await dispatch(addCompanyUserThunk({ companyId: selectedCompanyId, body: { fields, roleIds } }));

				if (addCompanyUserThunk.fulfilled.match(result)) {
					const added = result.payload?.data;
					if (result.payload?.queued) {
						success(t("User added offline — will sync when reconnected"));
					} else if (added?.rolesSyncedToKeycloak === false) {
						// Saved and in effect in the app; only the Keycloak copy of the roles is behind.
						warning(t("User added. Their roles could not be copied to Keycloak yet."));
					} else if (added?.outcome === "existingUser") {
						success(t("This person already had an account and was added to the company"));
					} else {
						success(t("User added successfully"));
					}
					// The list is reloaded by the thunk.
					dispatchState({ type: "CLOSE_ADD_DIALOG" });
				} else {
					logger.error("Failed to add user:", result.payload);
					ToastError(result.payload || t("Failed to add user. Please try again."), { severity: "error" });
				}
			} catch (e) {
				logger.error("Unexpected error in handleConfirmAdd:", e);
				ToastError(t("An unexpected error occurred"), { severity: "error" });
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[addFormFields, selectedCompanyId, dispatch, success, warning, ToastError, t]
	);

	const handleCancelAdd = useCallback(() => {
		dispatchState({ type: "CLOSE_ADD_DIALOG" });
	}, []);
	//#endregion

	// #region EDIT PROCESS
	// Fields from the application's "EditUser" form config, like Add. Non-editable fields (e.g. the ID
	// number, which is the username) are shown disabled; the backend refuses them anyway.
	const {
		fields: editFormFields,
		loading: editFormLoading,
		error: editFormError,
		notConfigured: editFormNotConfigured,
		reload: reloadEditForm,
	} = useFormFields("EditUser");

	// Roles are changed with "Modify roles" (region 4), not here.
	const editDialogFields = useMemo(() => editFormFields.filter((f) => f.storage !== "roles"), [editFormFields]);

	const handleEditClick = useCallback((row) => {
		if (editFormNotConfigured) {
			warning(t("The Edit user form is not configured for this application"));
			return;
		}
		if (editFormError) {
			ToastError(t("The Edit user form could not be loaded. Please try again."), { severity: "error" });
			reloadEditForm();
			return;
		}
		if (editFormLoading || editDialogFields.length === 0) {
			info(t("The form is still loading, please try again in a moment."));
			return;
		}

		const values = Object.fromEntries(editDialogFields.map((f) => [f.name, currentFieldValue(row, f)]));
		dispatchState({ type: "OPEN_EDIT_DIALOG", payload: { record: row, editFormData: values } });
	}, [editFormNotConfigured, editFormError, editFormLoading, editDialogFields, reloadEditForm, warning, info, ToastError, t]);

	const handleCancelEdit = useCallback(() => {
		dispatchState({ type: "CLOSE_EDIT_DIALOG" });
	}, []);

	// changedFields = only the fields that were modified (returnChangedFieldsOnly); nothing else is sent.
	const handleConfirmEdit = async (changedFields) => {
		const record = state.dialogs.selectedRecord;
		const fields = Object.fromEntries(
			Object.entries(changedFields || {}).filter(([name]) => editDialogFields.some((f) => f.name === name && !f.disabled))
		);
		if (!record || Object.keys(fields).length === 0) {
			dispatchState({ type: "CLOSE_EDIT_DIALOG" });
			return;
		}

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			const result = await dispatch(editCompanyUserThunk({ companyId: selectedCompanyId, userId: record.userId, body: { fields } }));

			if (editCompanyUserThunk.fulfilled.match(result)) {
				success(result.payload?.queued ? t("Saved offline — will sync when reconnected") : t("User edited successfully"));
				// The list is reloaded by the thunk.
				dispatchState({ type: "CLOSE_EDIT_DIALOG" });
			} else {
				logger.error("Failed to edit user:", result.payload);
				ToastError(result.payload || t("Failed to edit user. Please try again."), { severity: "error" });
			}
		} catch (e) {
			logger.error("Unexpected error in handleConfirmEdit:", e);
			ToastError(t("An unexpected error occurred"), { severity: "error" });
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};
	//#endregion

	// #region DELETE PROCESS
	const handleDeleteClick = useCallback((row) => {
		dispatchState({
			type: "OPEN_DELETE_DIALOG",
			payload: { ...row },
		});
	}, []);

	const handleCancelDelete = useCallback(() => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (state.dialogs.selectedRecord) {
			logger.info("state.dialogs.selectedRecord: ", state.dialogs.selectedRecord);
			handleRemove(state.dialogs.selectedRecord.userId);
		}
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, [state.dialogs.selectedRecord]);

	// Removes the member from this company only: their membership and roles here end; their account and
	// other companies stay.
	const handleRemove = async (userId) => {
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			const result = await dispatch(removeCompanyUserThunk({ companyId: selectedCompanyId, userId }));

			if (removeCompanyUserThunk.fulfilled.match(result)) {
				// Hidden at once; offline it stays hidden until the queued removal is sent and the list reloads.
				setDeletedRecordIds((prev) => new Set(prev).add(userId));
				if (result.payload?.queued) {
					success(t("Will be removed from the company when reconnected"));
				} else if (result.payload?.data?.rolesSyncedToKeycloak === false) {
					warning(t("User removed from the company. Their roles could not be updated in Keycloak yet."));
				} else {
					success(t("User removed from the company"));
				}
			} else {
				logger.error("Failed to remove the user from the company:", result.payload);
				ToastError(result.payload || t("Failed to remove the user. Please try again."), { severity: "error" });
			}
		} catch (e) {
			logger.error("Unexpected error in handleRemove:", e);
			ToastError(t("An unexpected error occurred"), { severity: "error" });
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};
	// #endregion

	// #region MANAGE USERS TABLE COLUMNS
	// Memoize actions to prevent unnecessary recalculations
	const actions = useMemo(() => ({
		onModifyRoles: handleModifyRolesClick,
		onDelete: handleDeleteClick,
		onEdit: handleEditClick,
	}), [handleModifyRolesClick, handleDeleteClick, handleEditClick]);

	// Context for conditional rendering (hide buttons for current user)
	const context = useMemo(() => ({
		currentUserId: userId,
	}), [userId]);

	// Use the dynamic columns hook
	const { columns, isLoading: columnsLoading } = useTableColumns('ManageUsers', {
		actions,
		context,
	});
	// #endregion

	// #region RETURN  COMPONENT

	/*     if (state.loading.isLoadingBackdrop) {
			return (
				<LoadingBackdrop
					open={state.loading.isLoadingBackdrop}
					message="Loading data, please wait..."
					variant="bars"
					color="greenGradient"
				/>
			);
		} */
	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Typography variant="h3" gutterBottom color="primary">
				{t("Manage Users")}
			</Typography>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("Manage the users of the selected company and their roles.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Toolbar: always visible, so the first user can be added from an empty list too. */}
			<Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
				<TableActionGroup sx={{ display: "flex", justifyContent: "flex-end" }}>
					<TableActionButton text={t("Add New User")} variant="contained" startIcon={<AddIcon />} onClick={() => handleAddClick()} />
				</TableActionGroup>

				<SearchFilterBar
					mode="frontend"
					searchValue={state.table.searchInput}
					onSearchChange={(v) => {
						dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
						setSearchQuery(v);
					}}
					showFilterButton={false}
					showSearchButton={false}
					searchPlaceholder={t("Search users...")}
				/>
			</Box>

			{loading && !companyUsersData?.length ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
					<Loader variant="dots" message={t("Loading...")} />
				</Box>
			) : error && !displayData.length ? (
				// Nothing to show and the load failed (e.g. no manage_users permission): say so, with a retry.
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" onClick={() => selectedCompanyId && dispatch(fetchCompanyUsersRequest({ companyId: selectedCompanyId }))}>
							{t("Retry")}
						</Button>
					}
				>
					{error}
				</Alert>
			) : !displayData.length ? (
				<DataContainer
					data={[]}
					columns={columns}
					columnsLoading={columnsLoading}
					emptyStateIcon={CalendarIcon}
					emptyStateTitle={t("No users in this company")}
					emptyStateSubtitle={t("Start adding people to your company")}
				/>
			) : (
				<>
					<Box sx={{ p: 2 }}>
						<DataContainer data={pagedData} columns={columns} columnsLoading={columnsLoading} showRowNumbers maxHeight={600} stickyHeader />
					</Box>

					<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
						<CustomPagination
							currentPage={state.table.currentPage}
							totalPages={totalPages}
							onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
							showFirstButton
							showLastButton
						/>
					</Box>
				</>
			)}
			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title={t("Remove from company")}
				message={t("Remove this user from the selected company? Their roles in this company end. Their account and their membership in other companies are kept, and they can be added again later.")}
				confirmText={t("Remove")}
				confirmColor="error"
				cancelText={t("Cancel")}
			/>
			<EnhancedConfirmationDialog
				open={state.dialogs.editConfirm}
				onClose={handleCancelEdit}
				onConfirm={handleConfirmEdit}
				title={t("Edit User")}
				confirmText={t("Save")}
				cancelText={t("Cancel")}
				showMessage={false}
				returnChangedFieldsOnly={true}
				defaultValues={state.forms.editUserForm}
				fields={editDialogFields}
			/>
			<EnhancedConfirmationDialog
				open={state.dialogs.modifyRolesConfirm}
				onClose={handleCancelModifyRoles}
				onConfirm={handleConfirmModifyRoles}
				title={t("Modify User Roles")}
				cancelText={t("Cancel")}
				confirmText={t("Save")}
				confirmColor="primary"
				showMessage={false}
				returnChangedFieldsOnly={true}
				defaultValues={state.forms.assignRoleFormData}
				fields={[
					{
						name: "Roles",
						label: t("Roles"),
						type: "dropdown",
						required: false,
						multiple: true,
						options: state.ui.roleOptions,
						placeholder: t("Select roles..."),
					},
				]}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.addConfirm}
				onClose={handleCancelAdd}
				onConfirm={handleConfirmAdd}
				title={t("Add New User")}
				cancelText={t("Cancel")}
				confirmText={t("Confirm")}
				confirmColor="primary"
				showMessage={false}
				defaultValues={state.forms.addUserForm}
				fields={addDialogFields}
			/>
			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);
	// #endregion
}
